import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import { FormField } from './FormField';
import styles from './Select.module.scss';

// ============================================
// TYPES
// ============================================

export type InputSize = 'sm' | 'md' | 'lg';

export interface SelectOption {
  label: string;
  value: string;
  icon?: ReactNode;
  description?: string;
  disabled?: boolean;
}

/**
 * `label` is typed `string` but callers build options off API rows whose name
 * can be absent. A blank row beats a `TypeError` unmounting the whole surface
 * into the error boundary (BUG-7189). Module scope so it stays off dep arrays.
 */
const labelOf = (o: SelectOption): string => (typeof o.label === 'string' ? o.label : '');

export interface SelectProps {
  options: SelectOption[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  isRequired?: boolean;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  loading?: boolean;
  searchable?: boolean;
  size?: InputSize;
  fullWidth?: boolean;
  id?: string;
  /** Accessible name for the trigger when the Select is used without a visible
   *  `label` (e.g. inline table-row selects). Applied as `aria-label`. Ignored
   *  when `label` is set — the built-in label names the combobox via
   *  `aria-labelledby` (a `role="combobox"` div is not labelable, so
   *  FormField's `<label htmlFor>` alone never names it — BUG-6985). */
  ariaLabel?: string;
  /** Muted text rendered inside the trigger before the selected value, naming
   *  what the control is for when it sits in a row of look-alike dropdowns
   *  (e.g. "Currency" before a flag + currency name). Not part of the
   *  accessible name — pair it with `label` or `ariaLabel`. */
  triggerPrefix?: ReactNode;
  noResultsText?: string;
  /** When provided, renders a "create" row in the dropdown for a search query
   *  that matches no existing option exactly. Receives the trimmed query. */
  onCreateOption?: (query: string) => void;
  /** Builds the create row's label. Defaults to `Add "<query>"` (or `Add new`
   *  when the query is empty). */
  createOptionLabel?: (query: string) => string;
  /** When true (and `onCreateOption` is set), the create row is always shown —
   *  even with an empty search and even when options exist — so users discover
   *  they can add a new option without having to type first. The row is fired
   *  with the current (possibly empty) trimmed query. */
  alwaysShowCreateOption?: boolean;
}

// ============================================
// ICONS
// ============================================

function ChevronIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path d="M14 8a6 6 0 0 0-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// ============================================
// COMPONENT
// ============================================

function Select({
  options,
  value,
  onChange,
  placeholder = 'Select item',
  label,
  isRequired = false,
  error,
  helperText,
  disabled = false,
  loading = false,
  searchable = false,
  size = 'md',
  fullWidth = true,
  id: idProp,
  ariaLabel,
  triggerPrefix,
  noResultsText = 'No results found',
  onCreateOption,
  createOptionLabel,
  alwaysShowCreateOption = false,
}: SelectProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  // Combobox accessible name: the trigger is a role="combobox" div, which
  // `<label htmlFor>` cannot name, so point at FormField's `${id}-label`
  // when a visible label exists; otherwise fall back to the literal ariaLabel.
  // Same association MultiSelect uses (BUG-6985).
  const resolvedLabelledby = label ? `${id}-label` : undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Focus the search input the moment it mounts — NOT a `useEffect([isOpen])`.
  // The dropdown (and this input) only renders once `useDropdownPosition`
  // resolves a non-null position, which itself lands via a state update
  // inside that hook's own effect — one render AFTER `isOpen` flips true. An
  // effect keyed on `[isOpen]` alone fires on the earlier render, before the
  // input exists, and never re-fires once it actually mounts. A callback ref
  // sidesteps the timing entirely by focusing on actual DOM attachment.
  const focusSearchInput = useCallback((node: HTMLInputElement | null) => {
    node?.focus();
  }, []);
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    setHighlightedIndex(-1);
  }, []);
  const dropdownPosition = useDropdownPosition(wrapperRef, isOpen, handleClose);

  const selectedOption = options.find((o) => o.value === value);

  const filteredOptions = useMemo(
    () =>
      searchable && search
        ? options.filter((o) => labelOf(o).toLowerCase().includes(search.toLowerCase()))
        : options,
    [options, searchable, search],
  );

  const trimmedSearch = search.trim();
  // Only meaningful for the create row — skip the scan entirely when no
  // `onCreateOption` is wired rather than walking every option on each render.
  const hasExactMatch = useMemo(
    () =>
      Boolean(onCreateOption) &&
      options.some((o) => labelOf(o).trim().toLowerCase() === trimmedSearch.toLowerCase()),
    [options, trimmedSearch, onCreateOption],
  );
  const showCreateOption =
    Boolean(onCreateOption) &&
    !hasExactMatch &&
    (alwaysShowCreateOption || trimmedSearch.length > 0);
  const createLabel = createOptionLabel
    ? createOptionLabel(trimmedSearch)
    : trimmedSearch
      ? `Add "${trimmedSearch}"`
      : 'Add new';

  const activateCreateOption = useCallback(() => {
    if (onCreateOption) {
      onCreateOption(trimmedSearch);
      handleClose();
    }
  }, [onCreateOption, trimmedSearch, handleClose]);

  // Reset highlighted index when search text changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);

  // Reset highlighted index when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setHighlightedIndex(-1);
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideWrapper && !insideDropdown) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0) {
      const el = optionRefs.current[highlightedIndex];
      if (el) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen((prev) => !prev);
    if (isOpen) setSearch('');
  };

  const handleSelect = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
    setSearch('');
  };

  const navCount = filteredOptions.length + (showCreateOption ? 1 : 0);

  const isIndexEnabled = useCallback(
    (idx: number): boolean => {
      if (showCreateOption && idx === filteredOptions.length) return true; // create row
      const opt = filteredOptions[idx];
      return !!opt && !opt.disabled;
    },
    [filteredOptions, showCreateOption],
  );

  /**
   * Find the next enabled index (real option OR the trailing create row) starting
   * from `from` in the given direction. Returns -1 if none.
   */
  const findNextEnabledIndex = useCallback(
    (from: number, direction: 1 | -1): number => {
      if (navCount === 0) return -1;
      let idx = from;
      for (let i = 0; i < navCount; i++) {
        idx = (idx + direction + navCount) % navCount;
        if (isIndexEnabled(idx)) return idx;
      }
      return -1;
    },
    [navCount, isIndexEnabled],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(false);
      setSearch('');
      return;
    }

    if (!isOpen) {
      // Open dropdown on arrow down/up or Enter when closed
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        e.preventDefault();
        if (!disabled && !loading) {
          setIsOpen(true);
        }
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      let nextIdx = findNextEnabledIndex(highlightedIndex, 1);
      // Skip already-selected item on first arrow press so highlight is visible
      if (highlightedIndex === -1 && nextIdx !== -1) {
        const opt = filteredOptions[nextIdx];
        if (opt && opt.value === value) {
          const skipIdx = findNextEnabledIndex(nextIdx, 1);
          if (skipIdx !== -1 && skipIdx !== nextIdx) nextIdx = skipIdx;
        }
      }
      if (nextIdx !== -1) setHighlightedIndex(nextIdx);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      let prevIdx = findNextEnabledIndex(highlightedIndex === -1 ? 0 : highlightedIndex, -1);
      // Skip already-selected item on first arrow press so highlight is visible
      if (highlightedIndex === -1 && prevIdx !== -1) {
        const opt = filteredOptions[prevIdx];
        if (opt && opt.value === value) {
          const skipIdx = findNextEnabledIndex(prevIdx, -1);
          if (skipIdx !== -1 && skipIdx !== prevIdx) prevIdx = skipIdx;
        }
      }
      if (prevIdx !== -1) setHighlightedIndex(prevIdx);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showCreateOption && highlightedIndex === filteredOptions.length) {
        activateCreateOption();
        return;
      }
      if (highlightedIndex >= 0) {
        const opt = filteredOptions[highlightedIndex];
        if (opt && !opt.disabled) {
          handleSelect(opt);
        }
      } else if (showCreateOption) {
        // Novel query typed, nothing highlighted → create it.
        activateCreateOption();
      }
    }
  };

  const triggerClasses = [
    styles.trigger,
    isOpen && styles.triggerOpen,
    error && styles.triggerError,
    disabled && styles.triggerDisabled,
  ]
    .filter(Boolean)
    .join(' ');

  const chevronClasses = [styles.chevron, isOpen && styles.chevronOpen].filter(Boolean).join(' ');

  const sizeClass = styles[size] ?? styles.md;
  const wrapperClasses = [styles.wrapper, sizeClass, fullWidth && styles.fullWidth]
    .filter(Boolean)
    .join(' ');

  return (
    <FormField
      id={id}
      label={label}
      isRequired={isRequired}
      helperText={helperText}
      error={error}
      isDisabled={disabled}
    >
      <div ref={wrapperRef} className={wrapperClasses} onKeyDown={handleKeyDown}>
        <div
          className={triggerClasses}
          onClick={handleToggle}
          role="combobox"
          aria-labelledby={resolvedLabelledby}
          aria-label={resolvedLabelledby ? undefined : ariaLabel}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-invalid={Boolean(error) || undefined}
          aria-required={isRequired || undefined}
          // Announce the linked error/helper text (FormField renders them with
          // these ids) — WP-14545: errors were role=alert'd but not linked.
          aria-describedby={error ? `${id}-error` : helperText ? `${id}-helper` : undefined}
          aria-disabled={disabled || undefined}
          tabIndex={disabled ? -1 : 0}
        >
          {triggerPrefix && !(searchable && isOpen) && (
            <span className={styles.triggerPrefix}>{triggerPrefix}</span>
          )}
          {searchable && isOpen ? (
            <input
              ref={focusSearchInput}
              className={styles.searchInput}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={selectedOption?.label ?? placeholder}
              onClick={(e) => e.stopPropagation()}
            />
          ) : selectedOption ? (
            <span className={styles.selectedValue}>
              {selectedOption.icon && (
                <span className={styles.optionIcon}>{selectedOption.icon}</span>
              )}
              <span className={styles.selectedLabel} title={selectedOption.label}>
                {selectedOption.label}
              </span>
            </span>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}

          {loading ? (
            <span className={styles.spinner}>
              <SpinnerIcon />
            </span>
          ) : (
            <span
              className={chevronClasses}
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
            >
              <ChevronIcon />
            </span>
          )}
        </div>
      </div>

      {isOpen &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            role="listbox"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              transform: dropdownPosition.placement === 'top' ? 'translateY(-100%)' : undefined,
            }}
          >
            {filteredOptions.length === 0 && !showCreateOption ? (
              <div className={styles.noResults}>{noResultsText}</div>
            ) : (
              <>
                {filteredOptions.map((option, index) => {
                  const isSelected = option.value === value;
                  const isHighlighted = index === highlightedIndex;
                  const optionClasses = [
                    styles.option,
                    isSelected && styles.optionSelected,
                    isHighlighted && styles.optionHighlighted,
                    option.disabled && styles.optionDisabled,
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <div
                      key={option.value}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      className={optionClasses}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
                      {option.description ? (
                        <div className={styles.optionContent}>
                          <span className={styles.optionLabel} title={labelOf(option)}>
                            {labelOf(option)}
                          </span>
                          <span className={styles.optionDescription} title={option.description}>
                            {option.description}
                          </span>
                        </div>
                      ) : (
                        <span className={styles.optionLabel} title={labelOf(option)}>
                          {labelOf(option)}
                        </span>
                      )}
                    </div>
                  );
                })}
                {showCreateOption && (
                  <div
                    ref={(el) => {
                      optionRefs.current[filteredOptions.length] = el;
                    }}
                    className={[
                      styles.option,
                      styles.createOption,
                      highlightedIndex === filteredOptions.length && styles.optionHighlighted,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    role="option"
                    aria-selected={false}
                    onClick={activateCreateOption}
                    onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
                  >
                    <span className={styles.createOptionPlus} aria-hidden="true">
                      +
                    </span>
                    <span className={styles.optionLabel}>{createLabel}</span>
                  </div>
                )}
              </>
            )}
          </div>,
          document.body,
        )}
    </FormField>
  );
}

export default Select;
